import { useState } from 'react';
import { BRIDGE_CAPABILITIES, type SmartFillBucket } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { useKeyedBridgeAction } from '../sidebar/data';
import { useLearningSession } from '../sidebar/learning';
import { InlineError } from './SidebarState';

/**
 * Says that learning changed the bucket and offers the version from before.
 * Restoring also discards edits made since, so it asks once before acting.
 */
export function LearnedChanges({
  bucket,
  onReverted
}: {
  bucket: SmartFillBucket;
  onReverted: (bucket: SmartFillBucket) => void;
}) {
  const { client } = useSidebarSession();
  const { capabilities } = useLearningSession();
  const revert = useKeyedBridgeAction<'revert'>();
  const [confirming, setConfirming] = useState(false);

  if (!bucket.hasLearnedChanges) return null;
  const canRevert = bucket.canEdit && capabilities.includes(BRIDGE_CAPABILITIES.REVERT_LEARNING);

  const restore = async () => {
    const reverted = await revert.run('revert', 'Could not restore this bucket.', () =>
      client.request('buckets.revertLearning', {
        bucketId: bucket.id,
        expectedRevision: bucket.revision
      })
    );
    if (!reverted) return;
    setConfirming(false);
    onReverted(reverted);
  };

  return (
    <section className="learning-feedback" aria-label="Learned changes">
      <div className="learning-feedback__heading">
        <span>Cara updated this bucket from your edits.</span>
        {canRevert && !confirming && (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setConfirming(true)}
          >
            Revert to original
          </button>
        )}
      </div>
      {confirming && (
        <div className="delete-confirmation" role="alertdialog" aria-label="Revert to original">
          <strong>Revert to original?</strong>
          <p>The bucket goes back to how it was before Cara learned. Later edits are lost.</p>
          <div>
            <button
              type="button"
              className="button button--quiet"
              disabled={revert.busy !== null}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--danger"
              disabled={revert.busy !== null}
              onClick={() => void restore()}
            >
              Revert
            </button>
          </div>
        </div>
      )}
      {revert.error && <InlineError message={revert.error.message} />}
    </section>
  );
}
